/**
 * Central catalog of all learning tags across the app.
 * Used by Microlearning topic picker, Play quiz topic hints, Learn Coach
 * interests, and any future personalization surface.
 *
 * Grouped so the UI can display them as collapsible categories instead of
 * one flat overwhelming list. Adding a new tag anywhere in the app? Put it
 * here so every surface picks it up automatically.
 */

export const TAG_GROUPS = [
  {
    id: "regions_france",
    label: "France",
    tags: [
      "Bourgogne", "Bordeaux", "Champagne", "Loire", "Rhône Nord", "Rhône Sud",
      "Alsace", "Languedoc", "Roussillon", "Provence", "Beaujolais", "Sud-Ouest",
      "Corse", "Jura", "Savoie",
    ],
  },
  {
    id: "regions_europe",
    label: "Europe (hors France)",
    tags: [
      "Italie — Piémont", "Italie — Toscane", "Italie — Vénétie", "Italie — Sicile",
      "Italie — Frioul", "Italie — Ombrie", "Espagne — Rioja", "Espagne — Ribera del Duero",
      "Espagne — Priorat", "Espagne — Jerez", "Espagne — Rías Baixas",
      "Portugal — Douro", "Portugal — Vinho Verde", "Portugal — Alentejo",
      "Allemagne — Moselle", "Allemagne — Rheingau", "Allemagne — Pfalz",
      "Autriche — Wachau", "Grèce — Santorin", "Hongrie — Tokaj", "Slovénie",
      "Angleterre — Sussex", "Suisse — Valais",
    ],
  },
  {
    id: "regions_americas",
    label: "Amériques",
    tags: [
      "Californie — Napa Valley", "Californie — Sonoma", "Californie — Central Coast",
      "Oregon — Willamette", "Washington State",
      "Chili — Colchagua", "Chili — Maipo", "Chili — Casablanca",
      "Argentine — Mendoza", "Argentine — Salta",
      "Uruguay", "Brésil — Serra Gaúcha",
      "Canada — Niagara", "Canada — Okanagan",
      "Mexique — Valle de Guadalupe",
    ],
  },
  {
    id: "regions_africa_asia",
    label: "Afrique, Asie & Océanie",
    tags: [
      "Afrique du Sud — Stellenbosch", "Afrique du Sud — Swartland", "Afrique du Sud — Franschhoek",
      "Australie — Barossa", "Australie — Margaret River", "Australie — Hunter Valley",
      "Nouvelle-Zélande — Marlborough", "Nouvelle-Zélande — Central Otago",
      "Chine — Ningxia", "Japon — Yamanashi", "Liban — Bekaa", "Israël", "Géorgie — Kakhétie",
      "Turquie", "Inde",
    ],
  },
  {
    id: "cepages",
    label: "Cépages",
    tags: [
      "Cabernet Sauvignon", "Merlot", "Pinot Noir", "Syrah", "Grenache", "Tempranillo",
      "Sangiovese", "Nebbiolo", "Malbec", "Zinfandel", "Gamay", "Mourvèdre",
      "Chardonnay", "Sauvignon Blanc", "Riesling", "Chenin Blanc", "Viognier",
      "Sémillon", "Albariño", "Gewurztraminer", "Assyrtiko", "Furmint",
    ],
  },
  {
    id: "spirits_whisky",
    label: "Whiskies",
    tags: [
      "Scotch Single Malt", "Scotch Blended", "Islay", "Speyside", "Highland",
      "Bourbon", "Rye", "Tennessee", "Irish Whiskey", "Japanese Whisky",
      "Whisky français", "Whisky indien",
    ],
  },
  {
    id: "spirits_grape",
    label: "Eaux-de-vie de raisin & fruits",
    tags: [
      "Cognac", "Armagnac", "Pisco", "Grappa", "Marc", "Brandy",
      "Calvados", "Kirsch", "Poire Williams", "Eau-de-vie de mirabelle",
    ],
  },
  {
    id: "spirits_cane_agave",
    label: "Rhums & Agaves",
    tags: [
      "Rhum agricole", "Rhum de mélasse", "Rhum tradition", "Rhum Vénézuela",
      "Rhum Jamaïque", "Cachaça", "Tequila blanco", "Tequila reposado",
      "Tequila añejo", "Mezcal",
    ],
  },
  {
    id: "spirits_botanicals",
    label: "Gins, vodkas & botaniques",
    tags: [
      "London Dry Gin", "Old Tom Gin", "Genièvre", "Vodka", "Aquavit", "Absinthe",
      "Pastis", "Ouzo", "Baijiu", "Soju", "Vermouth", "Amaro", "Chartreuse",
    ],
  },
  {
    id: "spirits_asia",
    label: "Spiritueux d'Asie",
    tags: ["Saké", "Shochu", "Awamori", "Umeshu"],
  },
  {
    id: "value_chain_amont",
    label: "Chaîne de valeur — Amont",
    tags: [
      "Viticulture", "Terroir", "Climatologie viticole", "Géologie & sols",
      "Sélection massale", "Porte-greffes", "Taille de la vigne",
      "Traitements phytosanitaires", "Viticulture biologique",
      "Biodynamie", "Vin nature", "Vendanges manuelles vs mécaniques",
    ],
  },
  {
    id: "value_chain_cave",
    label: "Chaîne de valeur — Cave",
    tags: [
      "Vinification en rouge", "Vinification en blanc", "Vinification en rosé",
      "Effervescence — méthode traditionnelle", "Effervescence — méthode Charmat",
      "Macération pelliculaire", "Macération carbonique",
      "Élevage sous bois", "Élevage sur lies", "Assemblage",
      "Sulfitage", "Chai & barriques", "Filtration & collage",
      "Mise en bouteille",
    ],
  },
  {
    id: "value_chain_aval",
    label: "Chaîne de valeur — Aval",
    tags: [
      "Étiquetage & régulation", "AOC / AOP / DOC / DO", "Négoce",
      "Distribution & CHR", "Œnotourisme", "Cave particulière",
      "Sommellerie", "Service du vin", "Accords mets & vins",
      "Marketing & branding", "Vente aux enchères",
      "Économie mondiale du vin", "Investissement & wine funds",
    ],
  },
  {
    id: "tasting",
    label: "Dégustation & analyse sensorielle",
    tags: [
      "Analyse visuelle", "Analyse olfactive — 1er nez", "Analyse olfactive — 2e nez",
      "Analyse gustative", "Arômes primaires", "Arômes secondaires", "Arômes tertiaires",
      "Structure tannique", "Acidité", "Sucrosité", "Alcool",
      "Défauts du vin (bouchon, oxydation, réduction)",
      "Dégustation à l'aveugle",
    ],
  },
];

// Flat list of all tags (used when you don't need the grouping).
export const ALL_TAGS = TAG_GROUPS.flatMap((g) => g.tags);
export const TAG_COUNT = ALL_TAGS.length;
